import { AuthenticatedHttp, HttpError, ResilientHttp, ThrowingHttp } from "@telarya/telarya-sdk"
import { message } from "ant-design-vue"

import { showArchivedCompanyError } from "@/components/Companies/archivedCompanyError"
import { selectedTenantId, tenantCompanies, requestTenantSelection } from "@/init/authState"
import { i18n } from "@/i18n"

const defaultResilientOptions = {
  maxRetries: 2,
  timeoutMs: 20000
}
const defaultInvoiceWriteTimeoutMs = 60000

const getFetchPathname = (input) => {
  const rawUrl = typeof input === "string" ? input : input?.url
  if (typeof rawUrl !== "string" || !rawUrl) return ""

  try {
    return new URL(rawUrl, "http://localhost").pathname.toLowerCase().replace(/\/+$/, "")
  } catch {
    return rawUrl.toLowerCase().split("?")[0].replace(/\/+$/, "")
  }
}

const getFetchMethod = (input, init) => {
  const method = init?.method ?? input?.method ?? "GET"
  return String(method).toUpperCase()
}

const isInvoiceCreateOrUpdateRequest = (input, init) => {
  const method = getFetchMethod(input, init)
  if (method !== "POST" && method !== "PUT") return false

  const pathname = getFetchPathname(input)
  if (!pathname) return false
  const hasInvoiceBasePath = pathname.endsWith("/invoice") || pathname.endsWith("/invoices")
  const hasInvoiceResourcePath = pathname.includes("/invoice/") || pathname.includes("/invoices/")

  const isCreateRequest = method === "POST" && hasInvoiceBasePath
  const isUpdateRequest = method === "PUT" && (hasInvoiceBasePath || hasInvoiceResourcePath)
  return isCreateRequest || isUpdateRequest
}

let tenantPromptInFlight = false

export const createAuthenticatedHttpClient = (tokenLoader, options = defaultResilientOptions) => {
  const resilientOptions = options?.resilientOptions ?? options ?? defaultResilientOptions
  const authOptions = {}
  const invoiceWriteTimeoutMs =
    typeof options?.invoiceWriteTimeoutMs === "number" ? options.invoiceWriteTimeoutMs : defaultInvoiceWriteTimeoutMs

  if (typeof options?.tenantIdProvider === "function") {
    authOptions.tenantIdProvider = options.tenantIdProvider
  }
  if (typeof options?.tenantHeaderName === "string") {
    authOptions.tenantHeaderName = options.tenantHeaderName
  }
  if (!authOptions.tenantIdProvider && selectedTenantId) {
    authOptions.tenantIdProvider = () => selectedTenantId.value
  }

  const throwingHttp = new ThrowingHttp(
    new ResilientHttp(new AuthenticatedHttp(tokenLoader, undefined, authOptions), resilientOptions)
  )
  const invoiceWriteResilientOptions = {
    ...resilientOptions,
    timeoutMs: Math.max(resilientOptions?.timeoutMs ?? defaultResilientOptions.timeoutMs, invoiceWriteTimeoutMs)
  }
  const invoiceWriteHttp =
    invoiceWriteResilientOptions.timeoutMs === resilientOptions?.timeoutMs
      ? throwingHttp
      : new ThrowingHttp(
          new ResilientHttp(
            new AuthenticatedHttp(tokenLoader, undefined, authOptions),
            invoiceWriteResilientOptions
          )
        )

  const promptTenantSelectionOnError = async (error) => {
    if (tenantPromptInFlight || error?.body?.code !== "tenant_required") return
    tenantPromptInFlight = true
    message.warning(i18n.global.t("tenants.selectionRequired"))
    try {
      await requestTenantSelection("switch")
    } finally {
      tenantPromptInFlight = false
    }
  }

  const originalFetch = throwingHttp.fetch.bind(throwingHttp)
  const invoiceWriteFetch = invoiceWriteHttp.fetch.bind(invoiceWriteHttp)
  throwingHttp.fetch = async (...args) => {
    const requestTenantId = authOptions.tenantIdProvider?.()
    const fetchImpl = isInvoiceCreateOrUpdateRequest(args[0], args[1]) ? invoiceWriteFetch : originalFetch
    try {
      return await fetchImpl(...args)
    } catch (error) {
      if (error instanceof HttpError) {
        promptTenantSelectionOnError(error)
        if (requestTenantId === authOptions.tenantIdProvider?.()) {
          showArchivedCompanyError(error, requestTenantId)
        }
      }
      throw error
    }
  }

  return throwingHttp
}
